const stats = [
  { stat: "10+ Years", label: "Application Security Experience", sub: "Across regulated industries" },
  { stat: "Accountable Expertise", label: "Senior security judgment", sub: "Clear ownership. Evidence-ready outcomes" },
  { stat: "4 Industries", label: "Logistics · Healthcare · Financial Services · SaaS", sub: "Regulated, audited, insured" },
  { stat: "3-Phase", label: "Assess. Remediate. Validate.", sub: "Repeatable methodology" },
];

// Anonymized client industry signals — kept abstract until logo permissions are secured.
// [CONFIRM] Replace these badges with named client wordmarks once written permission is granted.
const clientBadges = [
  { tier: "Fortune 500", sector: "US Health System" },
  { tier: "Top-50", sector: "US Financial Services" },
  { tier: "Series B", sector: "Clinical AI SaaS" },
  { tier: "Global", sector: "Logistics & Supply Chain" },
];

// [CONFIRM] Update certification status as audits progress.
const certifications = [
  { name: "SOC 2 Type II", status: "In progress — target Q4 2026" },
  { name: "ISO 27001", status: "Scoping" },
  { name: "Cyber Essentials Plus", status: "Planned 2027" },
];

const frameworks = [
  "OWASP",
  "MITRE ATLAS",
  "NIST AI RMF",
  "STRIDE",
  "HIPAA",
  "SOC 2",
  "CPRA/CCPA",
  "GDPR",
  "CMMC",
  "HITRUST",
];


export default function ArqProofBar() {
  return (
    <section className="bg-arq-bg py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="arq-label text-center mb-3">Why Us</div>
        <h2
          className="font-display font-semibold text-arq-text text-center mb-12 max-w-[820px] mx-auto"
          style={{ fontSize: "clamp(26px, 3.4vw, 36px)", lineHeight: 1.2 }}
        >
          Practitioner credibility. Repeatable methodology. Audit-ready evidence.
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          {stats.map((s) => (
            <div
              key={s.label}
              className="bg-arq-surface1 border border-arq-border rounded-xl p-6 md:p-7 text-center flex flex-col"
            >
              <div className="font-display font-bold text-[28px] md:text-[32px] text-arq-blue leading-none">
                {s.stat}
              </div>
              <div className="text-arq-text text-[14px] md:text-[15px] font-semibold mt-3 leading-snug">
                {s.label}
              </div>
              <div className="text-arq-muted text-[12px] mt-2 font-mono">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Sample finding card */}
        <div className="mb-16">
          <div className="arq-label text-center mb-3">What a finding looks like</div>
          <div className="max-w-[720px] mx-auto bg-arq-bg/80 border border-arq-border rounded-xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
            {/* Terminal header */}
            <div className="flex items-center justify-between px-4 py-3 bg-arq-surface1 border-b border-arq-border">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-arq-danger" />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#D97706" }} />
                <span className="w-2.5 h-2.5 rounded-full bg-arq-teal" />
              </div>
              <div className="font-mono text-[11px] text-arq-tertiary">ArqeraAI Assessment · redacted_sample</div>
            </div>

            {/* Terminal body */}
            <div className="p-5 md:p-6 font-mono text-[13px] md:text-[14px] space-y-4">
              <div className="grid grid-cols-[100px_1fr] md:grid-cols-[120px_1fr] gap-x-4 gap-y-3">
                <span className="text-arq-tertiary">Severity</span>
                <span className="text-arq-danger font-semibold">Critical</span>

                <span className="text-arq-tertiary">Finding</span>
                <span className="text-arq-text">Indirect prompt injection via vendor AI scribe</span>

                <span className="text-arq-tertiary">Regulatory Citation</span>
                <span className="text-arq-text">45 CFR 164.312(a)(1) — HIPAA Access Control</span>

                <span className="text-arq-tertiary">Dollar Exposure</span>
                <span className="text-arq-text">$1.2M–$1.9M OCR penalty range</span>

                <span className="text-arq-tertiary">Status</span>
                <span className="text-arq-teal font-semibold">Remediated</span>
              </div>
            </div>

            {/* Terminal footer */}
            <div className="px-5 py-3 border-t border-arq-border font-mono text-[11px] text-arq-tertiary">
              ArqeraAI Assessment · 3 critical findings
            </div>
          </div>
          <p className="text-arq-tertiary text-[12px] text-center mt-4 max-w-[640px] mx-auto leading-relaxed">
            Every finding in every report is documented this way.
          </p>
        </div>

        {/* Trust: anonymized client signal */}
        <div className="mb-12">
          <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary text-center mb-5">
            Engagements delivered for
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {clientBadges.map((b) => (
              <div
                key={`${b.tier}-${b.sector}`}
                className="bg-arq-surface1 border border-arq-border rounded-lg px-4 py-4 text-center"
              >
                <div className="font-mono-tech text-[10px] tracking-[0.1em] uppercase text-arq-blue mb-1">
                  {b.tier}
                </div>
                <div className="text-arq-text text-[13px] md:text-[14px] font-semibold leading-snug">
                  {b.sector}
                </div>
              </div>
            ))}
          </div>
          <p className="text-arq-tertiary text-[12px] text-center mt-4 max-w-[640px] mx-auto leading-relaxed">
            Client identifiers anonymized by engagement NDA. Named references and redacted deliverables available on request under counter-NDA.
          </p>
        </div>

        {/* Firm certifications */}
        <div className="mb-12">
          <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary text-center mb-5">
            Certifications in Progress
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 max-w-[820px] mx-auto">
            {certifications.map((c) => (
              <div
                key={c.name}
                className="bg-arq-surface1 border border-arq-border rounded-lg px-4 py-4 text-center"
              >
                <div className="text-arq-text text-[14px] font-semibold leading-snug">{c.name}</div>
                <div className="font-mono text-[11px] text-arq-muted mt-1.5">{c.status}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Frameworks */}
        <div className="mb-10">
          <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary text-center mb-5">
            Frameworks We Operate Within
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {frameworks.map((f) => (
              <span
                key={f}
                className="arq-tag font-mono text-[11px] text-arq-teal border-arq-teal/40"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
